/**
 * Deep Linking Service
 * Handles incoming deep links (Universal Links on iOS, App Links on Android)
 * 
 * Supports two app targets:
 * - Aria Chat (com.ariachat.mobile): handles app.ariachat.ai / beta.ariachat.ai links
 * - Portal (com.aptiveindex.mobile): handles portal.aptiveindex.com / app.aptiveindex.com links
 * 
 * When a user clicks a link like https://portal.aptiveindex.com/dashboard on their device,
 * this service ensures the app opens and navigates to the correct screen.
 */

import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';

/** Build-time flag — true when this is the Portal app build */
const isPortalTarget = process.env.REACT_APP_TARGET === 'portal';

/** Hostnames that belong to the Portal app */
const PORTAL_HOSTS = ['app.aptiveindex.com', 'app2.aptiveindex.com'];

/** Hostnames that belong to the Aria Chat app */
const ARIA_HOSTS = ['app.ariachat.ai', 'beta.ariachat.ai'];

export interface DeepLinkHandler {
  (url: string): void;
}

class DeepLinkingService {
  private handler: DeepLinkHandler | null = null;
  private urlOpenListener: PluginListenerHandle | null = null;

  /**
   * Registers the URL handler and ensures a single native `appUrlOpen` subscription
   * for the lifetime of the WebView (no remove on React effect cleanup).
   * Safe to call on every mount; updates `handler` when the router/navigate closure changes.
   */
  async registerHandler(handler: DeepLinkHandler): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    this.handler = handler;

    if (this.urlOpenListener) {
      return;
    }

    this.urlOpenListener = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      console.log('Deep link received:', event.url);

      if (this.handler) {
        this.handler(event.url);
      }
    });

    console.log('DeepLinkingService: appUrlOpen listener attached');
  }

  /**
   * Clears the handler so an unmounted router does not receive navigations.
   * Does not remove the native listener (avoids churn when StrictMode or deps re-run effects).
   */
  clearHandler(): void {
    this.handler = null;
  }

  /**
   * @deprecated Prefer registerHandler + clearHandler.
   */
  async initialize(handler: DeepLinkHandler): Promise<void> {
    await this.registerHandler(handler);
  }

  /**
   * Check if the app was launched with a deep link
   * Call this after initialization to handle cold-start deep links
   * 
   * @returns Promise that resolves with the launch URL, or null if none
   */
  async getLaunchUrl(): Promise<string | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    try {
      const result = await App.getLaunchUrl();
      return result?.url || null;
    } catch (error) {
      console.error('Error getting launch URL:', error);
      return null;
    }
  }

  /**
   * Parse a deep link URL and extract the path.
   * Works for both Aria Chat URLs and Portal URLs.
   * 
   * @param url - The deep link URL
   *   Aria:   'https://app.ariachat.ai/m/abc123'    → '/m/abc123'
   *   Portal: 'https://portal.aptiveindex.com/dashboard' → '/dashboard'
   * @returns The path portion with query string if present
   */
  parseDeepLinkUrl(url: string): string {
    try {
      const urlObj = new URL(url);

      let path = urlObj.pathname;

      // Remove /aria prefix if present (for localhost development of Aria builds)
      if (!isPortalTarget && path.startsWith('/aria')) {
        path = path.substring(5) || '/';
      }

      if (urlObj.search) {
        path += urlObj.search;
      }

      return path;
    } catch (error) {
      console.error('Error parsing deep link URL:', error);
      return url.startsWith('/') ? url : `/${url}`;
    }
  }

  /**
   * @deprecated Use parseDeepLinkUrl instead.
   * Kept for backwards compatibility with Aria Chat code.
   */
  parseAriaUrl(url: string): string {
    return this.parseDeepLinkUrl(url);
  }

  /**
   * Returns true if the given URL belongs to this app's known domains.
   * Prevents the app from trying to handle deep links meant for other apps.
   */
  isHandledUrl(url: string): boolean {
    try {
      const { hostname } = new URL(url);
      const knownHosts = isPortalTarget ? PORTAL_HOSTS : ARIA_HOSTS;
      return knownHosts.some(h => hostname === h || hostname.endsWith(`.${h}`));
    } catch {
      return false;
    }
  }

  /**
   * @deprecated Use clearHandler. Native listener is left registered intentionally.
   */
  async cleanup(): Promise<void> {
    this.clearHandler();
  }
}

// Export singleton instance
export const deepLinkingService = new DeepLinkingService();
export default deepLinkingService;
